/**
 * Login Page - Google Sign-In
 * Admin authentication via Google OAuth
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { useAuthStore } from '../store/auth';
import { AlertCircle, TreePalm } from 'lucide-react';

export default function LoginPage() {
  const [error, setError] = useState('');
  const { loginWithGoogle, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    setError('');
    if (!response.credential) {
      setError('Google sign-in failed. No credential received.');
      return;
    }

    try {
      await loginWithGoogle(response.credential);
      navigate('/dashboard');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Sign-in failed. Your account may not exist in the system.');
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in was cancelled or failed. Please try again.');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-10 space-y-3">
          <div className="flex items-center justify-center gap-2 mb-4">
            <TreePalm className="w-8 h-8 text-primary" />
            <h1 className="text-5xl font-semibold tracking-tight">Ishefa</h1>
          </div>
          <p className="text-muted-foreground text-lg">Business Management Platform</p>
        </div>

        {/* Login Card */}
        <div className="claude-card p-8 space-y-6">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold tracking-tight">Welcome back</h2>
            <p className="text-sm text-muted-foreground">
              Sign in with your Google account to access your dashboard
            </p>
          </div>

          {error && (
            <div className="claude-card border-destructive/50 bg-destructive/5 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive font-medium">{error}</p>
              </div>
            </div>
          )}

          <div className="flex justify-center">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-3">
                <span className="claude-spinner"></span>
                <span className="text-sm text-muted-foreground">Signing in...</span>
              </div>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                size="large"
                width="350"
                text="signin_with"
                shape="rectangular"
                theme="outline"
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-8">
          © 2025 Ishefa. All rights reserved.
        </p>
      </div>
    </div>
  );
}
